import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import useNav from "@/hooks/useNav";
import { useI18n } from "@/lib/i18n";

const BADGE_CLS = {
  NEW: "bg-emerald-500 text-white",
  HOT: "bg-red-600 text-white",
};

/**
 * Iter-219 quick-link pills bar — the 7-item highlight strip above the
 * category nav. On wide screens the pills sit centered with stretch-spacing
 * (`justify-around`); on narrow viewports the strip is horizontally
 * scrollable with the currently active pill scrolled into view. Order +
 * badges come from the admin `/api/classifieds/nav` payload; NEVER hard-
 * code the list.
 *
 * Iter-300 §2 — the nav feed now carries `external: true` + an absolute
 * href (e.g. https://www.allsale.events) directly, so we drop the
 * client-side overrides and honour the payload verbatim.
 */
export default function QuickPillsBar() {
  const q = useNav();
  const { t } = useI18n(); // Iter 704 — admin-fed pill labels follow the site language
  const pills = q.data?.pills || [];
  const { pathname } = useLocation();
  const listRef = useRef(null);
  const activeRef = useRef(null);

  // Match by href — treat exact match first, then longest-prefix match. This
  // keeps `/deals/foo` highlighting the `/deals` pill without accidentally
  // matching `/` for every route.
  const activeKey = React.useMemo(() => {
    if (!pills.length) return null;
    const exact = pills.find((p) => p.href === pathname);
    if (exact) return exact.key;
    const prefixed = pills
      .filter((p) => p.href !== "/" && pathname.startsWith(p.href))
      .sort((a, b) => b.href.length - a.href.length);
    return prefixed[0]?.key || null;
  }, [pills, pathname]);

  // On mount / when active changes, scroll the active pill into view on
  // narrow screens. Uses `inline: 'center'` so the current context sits in
  // the middle of the swipeable strip.
  useEffect(() => {
    if (!activeRef.current || !listRef.current) return;
    const list = listRef.current;
    // Only scroll if the list actually overflows (narrow viewports).
    if (list.scrollWidth <= list.clientWidth) return;
    const el = activeRef.current;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const target =
      list.scrollLeft + (elRect.left - listRect.left) - (list.clientWidth - elRect.width) / 2;
    list.scrollTo({ left: Math.max(0, target), behavior: "instant" });
  }, [activeKey]);

  if (!pills.length) return null;

  return (
    <nav
      className="w-full bg-[color:var(--brand-blue-wash)]/40 border-b border-soft"
      data-testid="quick-pills-bar"
      aria-label="Quick links"
    >
      <div className="max-w-7xl mx-auto px-2 md:px-8">
        <ul
          ref={listRef}
          className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar py-2 justify-start md:justify-center"
        >
          {pills.map((p) => {
            const isActive = p.key === activeKey;
            const external = !!p.external;
            const href = p.href;
            const cls = `inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3 py-1.5 border transition-colors whitespace-nowrap ${
              p.key === "sell"
                ? "bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] border-transparent text-white"
                : isActive
                ? "bg-[color:var(--brand-blue-wash)] border-[color:var(--brand-blue)] text-[color:var(--brand-blue)]"
                : "bg-white border-soft text-slate-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
            }`;
            const body = (
              <>
                {t(p.label)}
                {p.badge && BADGE_CLS[p.badge] && (
                  <span
                    data-testid={`pill-${p.key}-badge`}
                    className={`inline-flex items-center rounded-full text-[9px] font-black px-1.5 py-0.5 ${BADGE_CLS[p.badge]}`}
                  >
                    {p.badge}
                  </span>
                )}
              </>
            );
            return (
              <li
                key={p.key}
                ref={isActive ? activeRef : null}
                className="shrink-0"
              >
                {external ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pill-${p.key}`}
                    aria-current={isActive ? "page" : undefined}
                    className={cls}
                  >
                    {body}
                  </a>
                ) : (
                  <Link
                    to={href}
                    data-testid={`pill-${p.key}`}
                    aria-current={isActive ? "page" : undefined}
                    className={cls}
                  >
                    {body}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
